import { memo, useState } from 'react';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import urlParser from 'root/src/shared/util/urlParser';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { RedesignTwitterEmbed } from 'root/src/client/v2/common/components/RedesignTwitterEmbed';
import { FallbackMediaWrapper } from 'root/src/client/v2/video/components/FallbackMediaWrapper';
import { getDeliveryStartSeconds } from 'root/src/client/v2/_pages/dare/redesign/deliveryVideo';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { RedesignShareMenu } from 'root/src/client/v2/common/components/RedesignShareMenu';
import { RewardRemaining, isUncountableReward } from 'root/src/client/v2/common/components/RewardRemaining';
import { RewardAmountBadge } from 'root/src/client/v2/common/components/RewardAmountBadge';
import { DareTimeLeftChip } from 'root/src/client/v2/common/components/DareTimeLeftChip';
import { getDareTimeLeft } from 'root/src/client/v2/_pages/dare/redesign/viewDareStatus';
import { Badge } from 'root/src/client/v2/common/components/ui/badge';
import { RedesignLink } from 'root/src/client/v2/common/components/ui/link';
import { buildAssetUrl, assetSize } from 'root/src/client/v2/common/helpers/assetUrl';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { getReadableDarePlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
import format2Decimals from 'root/src/shared/util/format2Decimals';
import { getEmbedUrl } from 'root/src/client/v2/common/helpers/getEmbedUrl';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Img } from 'root/src/client/v2/common/components/ui/image';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignDeliverDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignDeliverDareModal';
import { RedesignRejectDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignRejectDareModal';
const getStatusBadge = (status) => {
    if (status === 'projectDelivered') {
        return { label: 'Completed', className: 'border-neon-green/40 bg-neon-green/10 text-neon-green' };
    }
    if (status === 'deliveryPending') {
        return { label: 'In Review', className: 'border-neon-amber/40 bg-neon-amber/10 text-neon-amber' };
    }
    return { label: 'Active', className: 'border-neon-pink/40 bg-neon-pink/10 text-neon-pink' };
};
const STATUS_NOT_DELIVERABLE = ['projectDelivered', 'rejected', 'deliveryRejected'];
// Cinematic redesign "project page" for a dare clip, shown in a modal (built from the card data,
// no extra fetch). Full-bleed media hero with the title/creator over a gradient; the hero play button
// swaps the thumbnail for an autoplaying embed, View details links to the legacy dare page.
export const RedesignDareDetailsModal = memo(({ clip, isOwnProfile, onChanged }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
    const modal = useRedesignModal();
    const [heroLoaded, setHeroLoaded] = useState(false);
    const [playing, setPlaying] = useState(false);
    const game = (_a = clip.games) === null || _a === void 0 ? void 0 : _a[0];
    const assignee = (_b = clip.assignees) === null || _b === void 0 ? void 0 : _b[0];
    const title = clip.title || (game === null || game === void 0 ? void 0 : game.name) || 'Dare';
    const delivery = (_c = clip.deliveries) === null || _c === void 0 ? void 0 : _c[0];
    const deliveryThumbnail = delivery === null || delivery === void 0 ? void 0 : delivery.thumbnail;
    // Mirrors DareVideoSection on the View Dare page: the clip only ever plays from the delivery's own
    // videoURL. `projectUrl` is the dare's identifier, not a video, so embedding it was never possible
    // and the hero fell through to a look-alike play circle that navigated away instead.
    const videoURL = delivery === null || delivery === void 0 ? void 0 : delivery.videoURL;
    const startSeconds = delivery ? getDeliveryStartSeconds(delivery) : 0;
    const embedUrl = videoURL ? getEmbedUrl(videoURL, { autoplay: true, start: startSeconds }) : '';
    const embeddedContent = delivery === null || delivery === void 0 ? void 0 : delivery.embeddedContent;
    // Twitter / X has no iframe embed (so getEmbedUrl returns ''); play it from the stored oEmbed HTML
    // or, when that is absent, the tweet URL itself.
    const isTwitter = Boolean(videoURL) && ((_d = urlParser(videoURL)) === null || _d === void 0 ? void 0 : _d.provider) === 'twitter';
    const tweetUrl = isTwitter ? videoURL : undefined;
    const hasTwitterEmbed = Boolean(embeddedContent) || Boolean(tweetUrl);
    const hasEmbed = Boolean(embedUrl) || hasTwitterEmbed;
    const thumbnail = deliveryThumbnail
        ? buildAssetUrl(deliveryThumbnail, { w: 1600, q: 90, fmt: 'webp' })
        : (_e = game === null || game === void 0 ? void 0 : game.boxArtTemplateUrl) === null || _e === void 0 ? void 0 : _e.replace('{width}x{height}', '1080x1440');
    const avatar = buildAssetUrl(assignee === null || assignee === void 0 ? void 0 : assignee.image, { w: assetSize('thumbnail'), q: 80, fmt: 'webp' });
    const badge = getStatusBadge(clip.status);
    const shareLink = clip.shareUrl || clip.projectUrl;
    const detailsUrl = clip.id ? `/dare/${clip.id}` : undefined;
    const platformLabel = (assignee === null || assignee === void 0 ? void 0 : assignee.platform) ? getReadableDarePlatform(assignee.platform) : undefined;
    const dropUrl = clip.campaignId ? routeUrls.dropRedesign(clip.campaignId) : undefined;
    const brandReward = clip.brandProduct;
    const rewardMeta = [brandReward === null || brandReward === void 0 ? void 0 : brandReward.keyTypeReadable, brandReward === null || brandReward === void 0 ? void 0 : brandReward.platformReadable].filter(Boolean).join(' · ');
    const { sponsor } = clip;
    const sponsorLogo = buildAssetUrl((_g = (_f = sponsor === null || sponsor === void 0 ? void 0 : sponsor.brandLogo) === null || _f === void 0 ? void 0 : _f.small) !== null && _g !== void 0 ? _g : (_h = sponsor === null || sponsor === void 0 ? void 0 : sponsor.brandLogo) === null || _h === void 0 ? void 0 : _h.big, { w: 96, q: 80, fmt: 'webp' });
    const pledgeAmount = (_j = clip.pledgeAmount) !== null && _j !== void 0 ? _j : 0;
    const goalAmount = ((_k = clip.assignees) !== null && _k !== void 0 ? _k : []).reduce((sum, current) => { var _a; return sum + ((_a = current.amountRequested) !== null && _a !== void 0 ? _a : 0); }, 0);
    const hasReward = pledgeAmount > 0 || goalAmount > 0;
    const rewardProgress = goalAmount > 0 ? Math.min(1, pledgeAmount / goalAmount) : (pledgeAmount > 0 ? 1 : 0);
    // Same expiration counter as the profile card, so the modal does not lose the "time left to
    // deliver" information the card shows.
    const showTimeLeft = getDareTimeLeft(clip) > 0;
    // Own-profile management: deliver an active dare's clip, or reject (matches the profile card kebab).
    const canManage = Boolean(isOwnProfile) && !STATUS_NOT_DELIVERABLE.includes((_l = clip.status) !== null && _l !== void 0 ? _l : '');
    const canDeliver = canManage && clip.status !== 'deliveryPending';
    const onDeliver = () => { var _a; return modal.show(<RedesignDeliverDareModal projectUrl={(_a = clip.projectUrl) !== null && _a !== void 0 ? _a : ''} onSuccess={onChanged}/>); };
    const onReject = () => { var _a; return modal.show(<RedesignRejectDareModal projectUrl={(_a = clip.projectUrl) !== null && _a !== void 0 ? _a : ''} onSuccess={onChanged}/>); };
    return (<RedesignModalShell className="sm:max-w-2xl">
			<div className="relative -mx-6 -mt-6 aspect-video w-[calc(100%+3rem)] shrink-0 overflow-hidden rounded-t-2xl bg-secondary sm:-mx-8 sm:-mt-8 sm:w-[calc(100%+4rem)]">
				{playing && hasEmbed && videoURL ? (
        // FallbackMediaWrapper plays DareDrop's hosted copy when the original clip is gone; the
        // query is authenticated, so a logged-out viewer just gets the embed.
        <FallbackMediaWrapper videoUrl={videoURL}>
						{hasTwitterEmbed && !embedUrl
                ? <RedesignTwitterEmbed embeddedContent={embeddedContent} tweetUrl={tweetUrl}/>
                : (<iframe src={embedUrl} title={title} allow="autoplay; fullscreen; encrypted-media; picture-in-picture" allowFullScreen className="h-full w-full border-0"/>)}
					</FallbackMediaWrapper>) : (<>
						{thumbnail && !heroLoaded && (<div className="absolute inset-0 animate-pulse bg-gradient-to-br from-secondary via-muted to-secondary"/>)}
						{thumbnail && (<Img src={thumbnail} alt={title} onLoad={() => setHeroLoaded(true)} className={cn('h-full w-full object-cover object-top transition-opacity duration-500', heroLoaded ? 'opacity-100' : 'opacity-0')}/>)}
						<div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent"/>

						<Badge variant="outline" className={`absolute left-4 top-4 backdrop-blur-md ${badge.className}`}>{badge.label}</Badge>

						{showTimeLeft && <DareTimeLeftChip expirationDate={clip.expirationDate} className="absolute right-4 top-4 z-10"/>}

						{hasEmbed && (<button type="button" onClick={() => setPlaying(true)} aria-label="Play clip" className="group absolute inset-0 flex items-center justify-center">
								<span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-neon-pink to-neon-purple text-white shadow-lg transition-transform group-hover:scale-110">
									<PlayArrowRoundedIcon sx={{ fontSize: 34 }}/>
								</span>
							</button>)}
						{!hasEmbed && videoURL && (<a href={videoURL} target="_blank" rel="noopener noreferrer" aria-label="Watch clip" className="group absolute inset-0 flex items-center justify-center">
								<span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-neon-pink to-neon-purple text-white shadow-lg transition-transform group-hover:scale-110">
									<PlayArrowRoundedIcon sx={{ fontSize: 34 }}/>
								</span>
							</a>)}

						<div className="pointer-events-none absolute inset-x-0 bottom-0 p-4 sm:p-5">
							<h2 className="font-display text-xl font-bold text-white sm:text-2xl">{title}</h2>
							<div className="mt-2 flex items-center gap-2">
								<div className="relative shrink-0">
									{avatar
                ? <Img src={avatar} alt={(_m = assignee === null || assignee === void 0 ? void 0 : assignee.displayName) !== null && _m !== void 0 ? _m : 'Creator'} className="h-7 w-7 rounded-full object-cover ring-1 ring-white/20"/>
                : <div className="h-7 w-7 rounded-full bg-secondary"/>}
									{(assignee === null || assignee === void 0 ? void 0 : assignee.platform) && (<span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-white">
											<DarePlatformIcon size="10" platform={assignee.platform} color={PlatformIconColor.Dark}/>
										</span>)}
								</div>
								<span className="text-sm font-medium text-white/90">
									{(_o = assignee === null || assignee === void 0 ? void 0 : assignee.displayName) !== null && _o !== void 0 ? _o : 'Creator'}{platformLabel ? ` · ${platformLabel}` : ''}
								</span>
							</div>
						</div>
						{shareLink && (<div className="absolute bottom-4 right-4 z-10">
								<RedesignShareMenu url={shareLink}/>
							</div>)}
					</>)}
			</div>

			<div className="flex w-full flex-col gap-4">
				{((brandReward === null || brandReward === void 0 ? void 0 : brandReward.name) || hasReward) && (<div className="rounded-xl border border-solid border-white/[0.08] bg-white/[0.02] p-4">
						<div className="mb-2 flex items-center gap-2">
							<CardGiftcardIcon sx={{ fontSize: 16 }} className="text-neon-green"/>
							<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">Reward</span>
						</div>
						{(brandReward === null || brandReward === void 0 ? void 0 : brandReward.name) && (<div className={hasReward ? 'mb-3' : ''}>
								<div className="flex items-start justify-between gap-2">
									<p className="min-w-0 text-sm font-semibold text-foreground">{brandReward.name}</p>
									{typeof brandReward.amount === 'number' && <RewardAmountBadge amount={brandReward.amount}/>}
								</div>
								{rewardMeta && <p className="mt-0.5 text-xs text-muted-foreground">{rewardMeta}</p>}
								<RewardRemaining capacity={clip.capacity} isUnlimited={isUncountableReward(brandReward.type)} className="mt-2"/>
							</div>)}
						{hasReward && (<>
								<div className="mb-1.5 flex items-center justify-between text-xs">
									<span className="font-semibold text-foreground">
										${format2Decimals(pledgeAmount)}{goalAmount ? ` of $${format2Decimals(goalAmount)}` : ''}
									</span>
									<span className="text-muted-foreground">
										{typeof clip.pledgers === 'number' ? `${clip.pledgers} pledger${clip.pledgers === 1 ? '' : 's'}` : 'Pledged'}
									</span>
								</div>
								<div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
									<div className="h-full rounded-full bg-gradient-to-r from-neon-pink to-neon-purple" style={{ width: `${rewardProgress * 100}%` }}/>
								</div>
								{goalAmount > 0 && (<p className="mt-1.5 text-[11px] text-muted-foreground">{Math.round(rewardProgress * 100)}% funded</p>)}
							</>)}
					</div>)}

				{clip.campaignName && dropUrl && (<RedesignLink href={dropUrl} className="flex items-center justify-between gap-3 rounded-xl border border-solid border-white/[0.08] bg-white/[0.02] p-3 transition-colors hover:border-neon-cyan/30">
						<div className="min-w-0">
							<p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">From the drop</p>
							<p className="truncate text-sm font-semibold text-foreground">{clip.campaignName}</p>
						</div>
						<span className="flex shrink-0 items-center gap-1 text-xs font-medium text-neon-cyan">
							View drop
							<ChevronRightRoundedIcon sx={{ fontSize: 16 }}/>
						</span>
					</RedesignLink>)}

				{(sponsor === null || sponsor === void 0 ? void 0 : sponsor.brandName) && (<div className="flex items-center gap-3 rounded-xl border border-solid border-white/[0.08] bg-white/[0.02] p-3">
						{sponsorLogo && <Img src={sponsorLogo} alt={sponsor.brandName} className="h-9 w-9 shrink-0 rounded-lg bg-white/5 object-contain p-1"/>}
						<div className="min-w-0">
							<p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">Sponsored by</p>
							{sponsor.brandWebsite
                ? (<a href={sponsor.brandWebsite} target="_blank" rel="noreferrer" className="truncate text-sm font-semibold text-foreground transition-colors hover:text-neon-cyan">
										{sponsor.brandName}
									</a>)
                : <p className="truncate text-sm font-semibold text-foreground">{sponsor.brandName}</p>}
						</div>
					</div>)}

				{(game === null || game === void 0 ? void 0 : game.name) && !(brandReward === null || brandReward === void 0 ? void 0 : brandReward.name) && !hasReward && (<p className="text-xs uppercase tracking-wide text-muted-foreground/70">{game.name}</p>)}

				{clip.description && <p className="whitespace-pre-line text-sm text-muted-foreground">{clip.description}</p>}

				{canManage && (<div className="flex items-center gap-3">
						{canDeliver && (<Button variant="brand" onClick={onDeliver} className="flex-1 rounded-xl py-3 text-sm font-semibold">
								<SendOutlinedIcon sx={{ fontSize: 16 }}/>
								Deliver clip
							</Button>)}
						{/* Tinted red, matching the Reject item in the clip card's kebab menu rather than the solid
            `destructive` button variant. hover:text-destructive is explicit so it beats ghost's hover:text-foreground. */}
						<Button variant="ghost" onClick={onReject} className="flex-1 rounded-xl bg-destructive/10 py-3 text-sm font-medium text-destructive hover:bg-destructive/20 hover:text-destructive">
							<CancelOutlinedIcon sx={{ fontSize: 16 }}/>
							Reject
						</Button>
					</div>)}
				{detailsUrl && (<div className="flex items-center gap-3">
						<RedesignLink href={detailsUrl} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-solid border-white/[0.12] bg-secondary py-3 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80">
							View details
							<ChevronRightRoundedIcon sx={{ fontSize: 16 }}/>
						</RedesignLink>
					</div>)}
			</div>
		</RedesignModalShell>);
});
